import { createContext, useContext, useState, useEffect } from 'react'

const ThemeContext = createContext()

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light'
  })

  const [accentColor, setAccentColor] = useState(() => {
    return localStorage.getItem('accentColor') || 'blue'
  })

  const [fontSize, setFontSize] = useState(() => {
    return localStorage.getItem('fontSize') || 'medium'
  })

  const [compactMode, setCompactMode] = useState(() => {
    return localStorage.getItem('compactMode') === 'true'
  })

  const [isTransitioning, setIsTransitioning] = useState(false)
  const [transitionOrigin, setTransitionOrigin] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const root = document.documentElement

    // Appliquer le thème
    if (theme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Appliquer la couleur d'accent
    root.setAttribute('data-accent', accentColor)

    // Appliquer la taille de police
    root.setAttribute('data-font-size', fontSize)

    // Appliquer le mode compact
    if (compactMode) {
      root.classList.add('compact')
    } else {
      root.classList.remove('compact')
    }

    // Sauvegarder dans localStorage
    localStorage.setItem('theme', theme)
    localStorage.setItem('accentColor', accentColor)
    localStorage.setItem('fontSize', fontSize)
    localStorage.setItem('compactMode', compactMode)
  }, [theme, accentColor, fontSize, compactMode])

  const changeThemeWithTransition = (newTheme, originElement) => {
    if (newTheme === theme) return

    // Obtenir la position de l'élément d'origine
    if (originElement) {
      const rect = originElement.getBoundingClientRect()
      setTransitionOrigin({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      })
    }

    setIsTransitioning(true)

    // Changer le thème après un court délai
    setTimeout(() => {
      setTheme(newTheme)
      setTimeout(() => {
        setIsTransitioning(false)
      }, 800)
    }, 100)
  }

  const changeAccentColor = (newColor) => {
    setAccentColor(newColor)
  }

  const changeFontSize = (newSize) => {
    setFontSize(newSize)
  }

  const toggleCompactMode = () => {
    setCompactMode(prev => !prev)
  }

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        changeThemeWithTransition,
        accentColor,
        changeAccentColor,
        fontSize,
        changeFontSize,
        compactMode,
        toggleCompactMode,
        isTransitioning,
        transitionOrigin
      }}
    >
      {children}

      {/* Overlay de transition circulaire */}
      {isTransitioning && (
        <div
          className="fixed inset-0 pointer-events-none z-[9999]"
          style={{
            background: theme === 'dark'
              ? 'radial-gradient(circle at var(--transition-x) var(--transition-y), transparent 0%, #1a202c 100%)'
              : 'radial-gradient(circle at var(--transition-x) var(--transition-y), transparent 0%, #ffffff 100%)',
            '--transition-x': `${transitionOrigin.x}px`,
            '--transition-y': `${transitionOrigin.y}px`,
            animation: 'expand-circle 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards'
          }}
        />
      )}
    </ThemeContext.Provider>
  )
}
